import {IDeferred, IPromise, IQService, ITimeoutService} from "angular";
import {regNavn} from "./tredjepartsTjenestenavn";
import {gettextCatalog} from "angular-gettext";
import _ from "lodash";
import {Debugger} from "debug";
import {Kontekst} from "felles/tjenester/Kontekst";
import {LokaltDatalager} from "felles/tjenester/LokaltDatalager";
import {erIkkeNill} from "felles/stoettefunk/nillsjekk";
import introJs from "intro.js";
import type {IntroJs} from "intro.js/src/intro"
import type {Options} from "intro.js/src/option";
import type {IntroStep} from "intro.js/src/core/steps";
import {DialogInterface, DialogMock} from "felles/tjenester/Dialog";
import {DetteErNyttDialogInterface, DetteErNyttDialogMock} from "felles/tjenester/modulspesifikke/DetteErNyttDialog";

const css = require('./IntroJs.scss');
const debug: Debugger = require('debug')('IntroJsTjeneste');

export class IntroJsTjeneste {

    static regNavn = "IntroJsTjeneste";
    private kjørendeIntroFullførte: boolean;

    static forInjisering() {
        return [
            regNavn.ng.$q,
            regNavn.ng.$timeout,
            LokaltDatalager.regNavn,
            Kontekst.regNavn,
            DialogMock.regNavn,
            DetteErNyttDialogMock.regNavn,
            regNavn.gettext.gettextCatalog,
            IntroJsTjeneste
        ]
    }

    constructor($q: IQService,
                private $timeout: ITimeoutService,
                private dataLager: LokaltDatalager,
                kontekst: Kontekst,
                private dialog: DialogInterface,
                detteErNyttDialog: DetteErNyttDialogInterface,
                private gettextCatalog: gettextCatalog) {
        this.initStatus = $q.defer<void>();

        // OBS: deaktiverer "allerede sett"-funksjonaliteten, så her får du Hjelp!
        const debugMode = false;

        $q.all([
          kontekst.erLastetFraServer(),
          detteErNyttDialog.erFerdig()
        ]).then(() => {
            this.avspilte = new Avspilte(kontekst, dataLager, $q).deaktiverLagring(debugMode);
            this.initDersomIkkeDeaktivert();
        })
    }

    private avspilte: Avspilte;
    private initStatus: IDeferred<void>;
    private deaktivert = false; // deaktiverer tjenesten i sin helhet. Brukt av E2E-tester
    private køedeIntroer: Introduksjon[] = [];
    private kjørendeIntro: IntroJs;
    private viserSlåAvDialog: boolean = false;

    /***************************************
     * OPPRETT, VIS OG LUKK INTRODUKSJONER
     **************************************/
    nyIntroduksjon(nøkkel: string) {
        return new Introduksjon(nøkkel);
    }

    /** vis tips - men med forsinkelse slik at domen har fått tid til å rendre */
    visIntroForsinket(introduksjon: Introduksjon, forsinkelse = 0) {
        this.$timeout(() => this.visIntro(introduksjon), forsinkelse);
    }

    visIntro(introduksjon: Introduksjon) {
        this.initStatus.promise.then(() => this.leggTilIKøOgSpillAvNeste(introduksjon));
    }

    exit() {
        this.kjørendeIntro = null;
        introJs().exit(false);
    }

    /***************************************
     * MANIPULER VISTE STEG
     ***************************************/
    nullstill() :IPromise<void> {
        return this.avspilte.nullstill();
    }

    markerStegSomIkkeVist(nøkler: string[]): IPromise<void> {
        return this.avspilte.nullstillSteg(nøkler);
    }

    markerAbsoluttAlleSomVist() :IPromise<void> {
        return this.avspilte.markerAbsoluttAlleSomVist();
    }

    leggTilIKø(introduksjon: Introduksjon) {
        debug('Legg til introduksjon uten å trigge avspilling', introduksjon.id())
        this.køedeIntroer.push(introduksjon);
    }

    // HJELPEMETODER


    /**
     * Legger til en introduksjon som skal vises
     *
     * Hvis samme introduksjon legges til i køen gjentatte ganger,
     * vil "avspilte steg" sørge for at den ikke faktisk vises til brukeren flere ganger
     * (med mindre det er endringer i betingede steg - da vil nye steg for brukeren vises)
     * I debugMode derimot, vil samme introduksjon vises flere ganger
     */
    private leggTilIKøOgSpillAvNeste(introduksjon: Introduksjon) {
        debug('Legg til introduksjon', introduksjon.id())

        this.leggTilIKø(introduksjon);
        this.konsumerFraKø();
    }

    /**
     * Viser neste introduksjon
     * (med mindre en annen allerede kjører)
     */
    private konsumerFraKø() {
        debug('konsumerFraKø', this.kjørendeIntro, this.køedeIntroer)
        if (this.deaktivert || !_.isNil(this.kjørendeIntro)) {
            debug(
              'konsumerer ikke, da intro allerede er aktiv (1), eller tjenesten er deaktivert (2)',
              !_.isNil(this.kjørendeIntro),
              this.deaktivert
            )
            return;
        }

        if (this.viserSlåAvDialog) {
            debug('konsumerer ikke, da vi viser slå av-dialogen');
            return;
        }

        const introduksjon = this.køedeIntroer.shift();
        if (!introduksjon) {
            debug('Ingen flere introduksjoner som skal vises');
            return;
        }

        const nyeSteg = this.avspilte.filtrerStegForBruker(introduksjon)
        if(nyeSteg.length == 0) {
            debug('Introduksjon '+ introduksjon.id() +' har kun steg brukeren allerede har sett')
            this.konsumerFraKø(); // aktiv intro er her tatt ut av køen. Fortsett til neste
            return;
        }

        /**
         * Marker stegene som vist, og så vis de.
         * Dette fordi en eventuell ny visning av introduksjonen kun vil vise stegene som ikke er vist til brukeren tidligere
         */
        this.avspilte.markerStegSomVist(nyeSteg)
          .then(() => this.renderIntro(introduksjon, nyeSteg));
    }

    renderIntro(introduksjon: Introduksjon, stegBrukerenIkkeHarSett: IntroduksjonsSteg[]) {
        const options = this.standardOppsett()

        // kjørendeIntroFullførte hjelper on(before)exit-kallene til å vite om introduksjonen var fullført eller ikke
        // dette pga manglende onexitbeforecomplete (eller lignende) i rammeverket
        this.kjørendeIntroFullførte = false;
        this.kjørendeIntro = introJs();
        // introduksjonens mulighet til å overskrive options
        if(introduksjon.harFørVisning()) {
            introduksjon.førVisning(this.kjørendeIntro, options);
        }
        // setter standard options + evt andre satt via førVisning-metoden over
        this.kjørendeIntro.setOptions(options);
        // legger til stegene brukeren skal se
        this.kjørendeIntro.addSteps(stegBrukerenIkkeHarSett.map(steg => steg.definisjon()))
        // introduksjonens mulighet til å hooke inn i dom-elementet når introduksjonen vises
        this.kjørendeIntro.onafterchange((element) => {
            if(introduksjon.harNårSynlig()) {
                introduksjon.erSynlig(
                  document.querySelector('.lederplan_tooltip .introjs-tooltiptext'),
                  this.kjørendeIntro.currentStep()
                )
            }
        })
        // legger inn markør som angir at onExit-callback skjer etter en fullført introduksjon
        this.kjørendeIntro.oncomplete((currentStep, reason) => {
            debug('onComplete trigget:', reason)
            this.kjørendeIntroFullførte = reason == "done" || reason == "end";
            if(this.kjørendeIntroFullførte && introduksjon.harNårFullført()) {
                introduksjon.nårFullført();
            }
        })
        // sjekk om brukeren vil deaktivere alle introduksjoner, og kall evt neste introduksjon
        this.kjørendeIntro.onexit(() => {
            if(!this.kjørendeIntroFullførte) {
                this.visSlåAvDialog()
            } else {
                this.kjørendeIntro = null
                this.konsumerFraKø()
            }
        });

        // let's goooo!
        this.kjørendeIntro.start();
        debug('Starter kjørendeIntro', this.kjørendeIntro)
    }

    visSlåAvDialog() {
        debug('Viser "vil du slå av?"')
        this.viserSlåAvDialog = true;
        this.dialog.confirm(
            this.gettextCatalog.getString('Vil du slå av alle veiledere?'),
            this.gettextCatalog.getString('Du kan når som helst skru de på igjen via spørsmålstegnet nederst på siden'),
            this.gettextCatalog.getString('Slå av alle'),
            this.gettextCatalog.getString('Avslutt denne')
        )
            .then(() => {
                this.avspilte.markerAbsoluttAlleSomVist()
                  .then(() => {
                      this.viserSlåAvDialog = false;
                      this.exit();
                  })
            })
            .catch(() => {
                debug('bruker ville ikke slå av veileder. Fortsetter til neste');
                this.viserSlåAvDialog = false;
                this.kjørendeIntro = null;
                this.konsumerFraKø();
            });
    }

    private standardOppsett(): Partial<Options> {
        return {
            disableInteraction: false, // To disable interactions with elements inside the highlighted box
            tooltipClass: 'lederplan_tooltip', //Adding CSS class to all tooltips
            exitOnOverlayClick: false, // Exit introduction when clicking on overlay layer, true or false
            overlayOpacity: 0.3, //Adjust the overlay opacity, Number
            nextLabel: this.gettextCatalog.getString('Neste'), //Next button label
            prevLabel: this.gettextCatalog.getString('Tilbake'), // Previous button label
            doneLabel: this.gettextCatalog.getString('Skjønner!'), // Done button label
        };
    }

    private initDersomIkkeDeaktivert():IPromise<void> {
        // Override for E2E-tester
        this.dataLager.hentEllerStandardVerdi('e2e.introjsDeaktivert', this.deaktivert)
          .then((verdi) => {
              this.deaktivert = !!verdi;
          })
          .finally(() => {
              if (this.deaktivert){
                  debug('IntroJS er deaktivert');
                  return this.initStatus.reject();
              }
              this.initStatus.resolve()
          });

        return this.initStatus.promise;
    }
}

export class Introduksjon {
    constructor(private nøkkel: string) {
    }

    private _steg: IntroduksjonsSteg[] = [];
    private _nårSynlig: (element: Element, aktivtStegNummer: number | undefined) => void
    private _førVisning: (introJs:IntroJs, standardOptions:Partial<Options>) => void
    private _nårFullført: () => void;

    leggTilSteg(nummer: number,
                steg: Partial<IntroStep>,
                visKunDersom: () => boolean = () => true): Introduksjon {
        this._steg.push(new IntroduksjonsSteg(nummer, this.nøkkel, steg, visKunDersom));
        return this;
    }

    id() {
        return this.nøkkel;
    }

    steg(): IntroduksjonsSteg[] {
        return this._steg;
    }

    hentAlleStegSomSkalVises() {
        return this.steg().filter(steg => steg.skalVises());
    }

    harNårSynlig() {
        return erIkkeNill(this._nårSynlig)
    }

    erSynlig(element: Element, aktivtStegNummer: number | undefined) {
        return this._nårSynlig(element, aktivtStegNummer);
    }

    kallNårSynlig(nårVist: (element: Element, aktivtStegNummer: number | undefined) => void) {
        this._nårSynlig = nårVist;
        return this;
    }

    harFørVisning() {
        return erIkkeNill(this._førVisning)
    }

    førVisning(introJs: IntroJs, options: Partial<Options>) {
        return this._førVisning(introJs, options);
    }

    kallFørVisning(førVisning: (introJs: IntroJs, options: Partial<Options>) => void) {
        this._førVisning = førVisning;
        return this;
    }

    kallNårFullført(nårFullført: () => void) {
        this._nårFullført = nårFullført;
        return this;
    }

    harNårFullført() {
        return erIkkeNill(this._nårFullført)
    }

    nårFullført() {
        return this._nårFullført();
    }
}

class IntroduksjonsSteg {
    constructor(private nummer: number,
                private introduksjonId: string,
                private introStep: Partial<IntroStep>,
                private visKunDersom: () => boolean) {
        this.introStep.step = nummer;
    }

    skalVises() {
        return this.visKunDersom();
    }

    definisjon() {
        return this.introStep;
    }

    id() {
        return this.introduksjonId + "." + this.nummer;
    }
}

/**
 * Holder styr på avspilte introduksjoner for brukeren
 *
 * OBS: sjekker ikke at kontekst allerede er lastet fra server,
 * og skal kun brukes etter dette har skjedd!
 * IntroJsTjeneste holder styr på dette.
 *
 * Motivasjonen for å holde en lokal kopi av avspilte, er for å kunne teste betingede introduksjonssteg
 * hvor introduksjonen legges i kø flere ganger, men ikke skal vises fordi brukeren har sett stegene.
 */
class Avspilte {
    private _debug: Debugger = require('debug')('IntroJsTjeneste:Avspilte');

    constructor(private kontekst: Kontekst,
                private dataLager: LokaltDatalager,
                private $q: IQService) {
        this._debug('avspilte introduksjoner fra brukervalg:', {...this.kontekst.brukervalg().introduksjoner.avspilte})
        this._avspilte = {...this.kontekst.brukervalg().introduksjoner.avspilte}
        this.overførAvspilteNøklerFraGammeltSystemPåMobil();
    }

    private lagringDeaktivert: boolean = false;
    private _avspilte: {[stegId:string]:boolean}

    /**
     * Deaktiverer faktisk lagring av avspilte introduksjonssteg
     *
     * OBS: fører til at _avspilte og brukervalg avviker mellom sidelastinger
     * Dette for at intensjonell dobbelt-køing av introduksjoner skal funke,
     * og at man skal slippe å nullstille mellom hver sidelasting
     */
    deaktiverLagring(skalDeaktiveres: boolean = true) {
        this._debug("erDeaktivert", skalDeaktiveres)
        this.lagringDeaktivert = skalDeaktiveres;
        return this;
    }

    /**
     * Returnerer muterbar kopi av allerede avspilte introduksjoner for denne brukeren
     */
    hent() {
        this._debug('hent', this._avspilte)
        return this._avspilte
    }

    /**
     * Markerer steg som vist.
     *
     * OBS: Hvis lagring er deaktivert, vil det ikke persisteres til brukervalg, men lokal tilstand vil anse steget som vist
     */
    markerStegSomVist(stegSomSkalMarkeresVist:IntroduksjonsSteg[]): IPromise<void> {
        stegSomSkalMarkeresVist.forEach(steg => this._avspilte[steg.id()] = true)

        return this.lagre()
    }

    filtrerStegForBruker(introduksjon: Introduksjon) {
        return introduksjon.hentAlleStegSomSkalVises()
          .filter(steg => this._avspilte[steg.id()] !== true)
    }

    lagre(ignorerDeaktivering: boolean = false): IPromise<void> {
        if(this.lagringDeaktivert && !ignorerDeaktivering) {
            this._debug('lagrer ikke, da lagring er deaktivert. Forsøkte lagre:', this._avspilte)
            return this.$q.resolve()
        }

        this._debug('lagre', this._avspilte)
        this._debug('(ignorerte deaktivering)');

        return this.kontekst.oppdaterBrukervalg({introduksjoner: {avspilte: this._avspilte}})
          .then(() => this.$q.resolve())
    }

    nullstill(): IPromise<void> {
        this._avspilte = {}
        return this.lagre(true)
    }

    nullstillSteg(nøkler: string[]): IPromise<void> {
        nøkler.forEach(nøkkel => this._avspilte[nøkkel] = false)
        return this.lagre();
    }


    /**
     * Innført i overgangen fra SP2 -> SP3.
     * Gir brukere mulighet til å markere alle som sett,
     * slik at erfarne SP3-brukere plutselig slipper å trykke seg gjennom en skog av introduksjoner.
     * På denne måten kan vi likevel pushe nye bokser som alle får se, og avhjelpe potensielle supportbehov.
     */

    markerAbsoluttAlleSomVist(): IPromise<void> {
        this._debug('Marker absolutt alle som vist')
        const avspilte: {[stegId:string]:boolean} = {};

        const steg = new Map<String, number>([
            ['arkiv', 5],
            ['beslutninger', 3],
            ['dashboard.oversikt', 12],
            ['gruppe', 6],
            ['grupper', 4],
            ['kommentarer', 3],
            ['møteside', 5],
            ['møteside.admin', 10],
            ['oppgaver', 7],

            // mobil
            ['aktiviteterKontroller', 8],
            ['møteKontroller-møtemeny', 1],
            ['sakKontroller-sakmeny', 1]
        ]);
        steg.forEach((antallSteg, nøkkel) => {
            for(let i=1; i<= antallSteg; i++) {
                avspilte[nøkkel + '.' + i] = true;
            }
        })

        avspilte['init.spøromvikanhjelpe.1'] = true;

        this._avspilte = avspilte;
        return this.lagre(true);
    }

    /**
     * Midlertidig overføringsmekanisme fra gammel til ny implementasjon i April 2024
     * Kan fjernes når vi tror de fleste har blitt overført, eller når det er innafor at folk som
     * har sett de før får se de igjen.
     * Gjelder kun mobil, da web fikk nye introduksjoner, og ikke overfører nøkler.
     */
    private overførAvspilteNøklerFraGammeltSystemPåMobil() {
        debug('overførAvspilteNøklerFraGammeltSystemPåMobil')
        // Allerede overført / har aldri sett introene
        if(!this.dataLager.eksisterer('avspilteIntrojs')) {
            return;
        }
        this.dataLager.hentEllerStandardVerdi('avspilteIntrojs', {})
            .then((avspilte: {[nøkkel:string]:boolean}) => {
                // Hadde lagret tomt sett (rart, men teoretisk mulig). Fjern nøkkel og move on
                if(Object.keys(avspilte).length == 0) {
                    this.dataLager.slett('avspilteIntrojs')
                    return;
                }
                for (let nøkkel in avspilte) {
                    switch (nøkkel) {
                        case 'aktiviteterKontroller':
                            this._avspilte['aktiviteterKontroller.1'] = avspilte[nøkkel];
                            this._avspilte['aktiviteterKontroller.2'] = avspilte[nøkkel];
                            this._avspilte['aktiviteterKontroller.3'] = avspilte[nøkkel];
                            this._avspilte['aktiviteterKontroller.4'] = avspilte[nøkkel];
                            this._avspilte['aktiviteterKontroller.5'] = avspilte[nøkkel];
                            this._avspilte['aktiviteterKontroller.6'] = avspilte[nøkkel];
                            this._avspilte['aktiviteterKontroller.7'] = avspilte[nøkkel];
                            this._avspilte['aktiviteterKontroller.8'] = avspilte[nøkkel];
                            break;
                        case 'møteKontroller-møtemeny':
                            this._avspilte['møteKontroller-møtemeny.1'] = avspilte[nøkkel];
                            break;
                        case 'sakKontroller-sakmeny':
                            this._avspilte['sakKontroller-sakmeny.1'] = avspilte[nøkkel];
                            break;
                    }
                }
                this.lagre().then(() => this.dataLager.slett('avspilteIntrojs'));
            })
    }
}
